import { forwardRef, useCallback } from 'react'

import { NumberFormatValues, NumericFormat } from 'react-number-format'

import ArcadeInput, { ArcadeInputProps } from './ArcadeInput'

export interface ArcadeMoneyInputProps extends Omit<ArcadeInputProps, 'onChange' | 'defaultValue' | 'value' | 'type'> {
  /** Currency symbol to display before the value (defaults to '$') */
  currencySymbol?: string
  /** Callback when the numeric value changes (without formatting) */
  onChange: (value: string) => void
  /** Only allow input in the form of dollars and cents, dollars, or cents */
  denominationOption?: 'DOLLARS_AND_CENTS' | 'DOLLARS_ONLY' | 'CENTS_ONLY'
  value: string
}

/**
 * Shows `emptyValue` while the input has no value, e.g. a lone `$` before the user starts typing an amount.
 */
const InputWithEmptyValue = forwardRef<HTMLInputElement, ArcadeInputProps & { emptyValue: string }>(
  ({ emptyValue, value, ...props }, ref) => (
    <ArcadeInput {...props} value={value === '' ? emptyValue : value} ref={ref} />
  )
)

InputWithEmptyValue.displayName = 'InputWithEmptyValue'

const ArcadeMoneyInput = forwardRef<HTMLInputElement, ArcadeMoneyInputProps>(
  (
    { onChange, currencySymbol = '$', disabled, denominationOption = 'DOLLARS_AND_CENTS', name, value, ...restProps },
    ref
  ) => {
    const validateNumericWithDecimal = useCallback(
      (values: NumberFormatValues) => {
        const { floatValue } = values
        if (floatValue === undefined) return true

        if (floatValue < 0) return false
        if (denominationOption === 'CENTS_ONLY' && floatValue >= 1) return false

        return true
      },
      [denominationOption]
    )

    const decimalScale = denominationOption === 'DOLLARS_ONLY' ? 0 : 2
    const thousandSeparator = denominationOption === 'CENTS_ONLY' ? false : true

    return (
      <NumericFormat
        customInput={InputWithEmptyValue}
        emptyValue={restProps.placeholder ? '' : currencySymbol}
        value={value}
        disabled={disabled}
        inputMode="decimal"
        thousandSeparator={thousandSeparator}
        decimalScale={decimalScale}
        // enforce two decimal places once user has shown intention to add cents
        fixedDecimalScale={value.includes('.')}
        allowNegative={false}
        prefix={currencySymbol}
        isAllowed={validateNumericWithDecimal}
        onValueChange={(values: NumberFormatValues) => {
          onChange(values.value)
          if ((values.floatValue === 0 || !values.floatValue) && denominationOption !== 'CENTS_ONLY') {
            onChange('')
          }
        }}
        getInputRef={ref}
        {...restProps}
      />
    )
  }
)

ArcadeMoneyInput.displayName = 'ArcadeMoneyInput'

export default ArcadeMoneyInput
